/**
 * ч.1296 — список звернень до комісії ДПС щодо зупиненої реєстрації ПН/РК
 * (пояснення / додаткові пояснення / скарги) зі станом подання і рішенням комісії.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { erpnAppeals, KIND_SHORT, RESULT_LABELS, type ErpnAppeal, type ErpnAppealKind } from '@/api/erpnAppeals';
import { orgs as orgsApi } from '@/api/catalogs';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { SUB_STATUS_UK } from '@/shared/components/ErpnAppealsCard';

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('uk-UA') : '');

export default function ErpnAppealsPage() {
  const navigate = useNavigate();
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } = usePermissionsByOrg('tax.invoice.manage');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  const [orgId, setOrgId] = useOrgFilter();
  const [kind, setKind] = useState<'' | ErpnAppealKind>('');

  const { datasource, total } = useInfiniteGridDatasource<ErpnAppeal>(
    (limit, offset, sort) => erpnAppeals.list({
      organization_id: orgId || undefined, kind: kind || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, kind],
  );

  const cols = useMemo<ColDef<ErpnAppeal>[]>(() => [
    { field: 'kind', headerName: 'Вид', width: 170,
      valueFormatter: (p) => p.value ? KIND_SHORT[p.value as ErpnAppealKind] : '' },
    { field: 'document_date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => fmtDate(p.value) },
    { colId: 'invoice', headerName: 'ПН/РК', width: 190, sortable: false,
      valueGetter: (p) => p.data?.invoice_number
        ? `${p.data.invoice_is_correction ? 'РК' : 'ПН'} № ${p.data.invoice_number} від ${fmtDate(p.data.invoice_date)}`
        : '' },
    { field: 'contractor_name', headerName: 'Покупець', width: 220, sortable: false },
    { field: 'organization_id', headerName: 'Організація', width: 200, sortable: false,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '' },
    { field: 'attachments_count', headerName: 'Копій', width: 80, sortable: false, type: 'rightAligned' },
    { colId: 'submission', headerName: 'Подання', width: 190, sortable: false,
      valueGetter: (p) => p.data ? (SUB_STATUS_UK[p.data.submission_status ?? ''] ?? 'не подано') : '' },
    { colId: 'result', headerName: 'Рішення комісії', width: 220, sortable: false,
      cellRenderer: (p: { data?: ErpnAppeal }) => {
        const r = p.data?.result;
        if (!r) return <span style={{ color: 'var(--muted)' }}>—</span>;
        const color = r === 'REGISTERED' ? 'var(--badge-green-fg)'
          : r === 'REFUSED' ? 'var(--badge-red-fg)' : 'var(--badge-amber-fg)';
        return <span style={{ color, fontWeight: 600 }}>{RESULT_LABELS[r]}</span>;
      } },
    { field: 'created_at', headerName: 'Створено', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '' },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Оскарження зупинення реєстрації ПН/РК<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/erpn-appeals/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій` : undefined}>
          + Пояснення та копії документів
        </button>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as '' | ErpnAppealKind)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">— усі види —</option>
          {(Object.keys(KIND_SHORT) as ErpnAppealKind[]).map((k) => <option key={k} value={k}>{KIND_SHORT[k]}</option>)}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<ErpnAppeal>
        deleteRow={{ remove: (id) => erpnAppeals.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="erpn-appeals"
        onRowDoubleClicked={(r) => navigate(`/erpn-appeals/${r.id}`)}
      />
    </div>
  );
}
